// src/components/LieferscheinPackzettel.tsx
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArtikelThumbnail from './ArtikelThumbnail';
import { fetchApi } from './fetchApi';


const ArtikelThumbnailAny = ArtikelThumbnail as any;

interface Position {
  id: number;
  artikel_id: number;
  menge: number;
  sku: string;
  bezeichnung: string;

  // neu (damit Thumbnail angezeigt werden kann)
  bild_url?: string | null;
}

interface Lieferschein {
  id: number;
  lieferschein_nr: string;
  status: string;
  erstellt_am: string;
  quelle_name: string;
  empfaenger: string;
  bemerkung: string | null;

  quelle_typ: string;
  quelle_id: number | null;

  positionen: Position[];
}

interface BoxEintrag {
  artikel_id: number;
  boxnummer: string;
  menge: number;
  lagerort_id: number;

  regalnummer?: string | null;
  fachnummer?: string | null;
}

export default function LieferscheinPackzettel() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [daten, setDaten] = useState<Lieferschein | null>(null);
  const [boxen, setBoxen] = useState<BoxEintrag[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    async function lade() {
      try {
        // Lieferschein-Details
        const lsRes = await fetchApi(`/api/lieferschein/view/${id}`);
        const lsJson = await lsRes.json();
        if (!lsRes.ok) throw new Error('Fehler beim Laden des Lieferscheins');
        setDaten(lsJson);

        // Lagerstand pro Box
        const boxRes = await fetchApi('/api/view/lagerstand-pro-box');
        const boxJson = await boxRes.json();
        setBoxen(Array.isArray(boxJson) ? boxJson : []);
      } catch (err: any) {
  console.error(err);
  setFehler(
    err?.message === '401'
      ? 'Session abgelaufen. Bitte neu einloggen.'
      : 'Fehler beim Laden der Daten.'
  );
} finally {
  setLaden(false);
}

    }

    lade();
  }, [id]);

  if (laden) {
    return (
      <Container>
        <Box mt={5} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (fehler || !daten) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">{fehler || 'Keine Daten gefunden.'}</Alert>
        </Box>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/lieferschein-liste')}>
          Zurück
        </Button>
      </Container>
    );
  }

  const gesamtSoll = daten.positionen.reduce((sum, pos) => sum + pos.menge, 0);

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      {/* Kopf mit Buttons – Buttons in Print ausblenden */}
      <Box
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          '@media print': {
            mb: 1,
          },
        }}
      >
        <Typography variant="h5" sx={{ '@media print': { fontSize: '1.2rem' } }}>
          Packzettel – Lieferschein {daten.lieferschein_nr}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            '@media print': {
              display: 'none',
            },
          }}
        >
          <Button variant="outlined" onClick={() => navigate(`/lieferschein/${daten.id}`)}>
            Zurück
          </Button>
          <Button variant="contained" onClick={() => window.print()}>
            Drucken
          </Button>
        </Box>
      </Box>

      {/* Kopf-Infos für den Ausdruck */}
      <Box mb={2}>
        <Typography variant="subtitle1">
          Sender: <strong>{daten.quelle_name}</strong>
        </Typography>
        <Typography variant="subtitle1">
          Empfänger: <strong>{daten.empfaenger}</strong>
        </Typography>
        <Typography variant="subtitle2">
          Erstellt am: {new Date(daten.erstellt_am).toLocaleString('de-AT')}
        </Typography>
        <Typography variant="subtitle2">Status: {daten.status}</Typography>
        {daten.bemerkung && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Bemerkung: {daten.bemerkung}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Gesamt Soll-Menge: <strong>{gesamtSoll}</strong>
        </Typography>
      </Box>

      {/* Hinweis nur auf Bildschirm */}
      <Box
        mb={2}
        sx={{
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Alert severity="info">
          Dies ist eine druckoptimierte Packübersicht. Beim Ausdruck werden nur relevante Inhalte
          ohne Buttons dargestellt.
        </Alert>
      </Box>

      {/* Positionen mit Boxen – komprimiert für Druck */}
      {daten.positionen.map((pos) => {
        const boxenFuerArtikel = boxen
          .filter(
            (b) =>
              b.artikel_id === pos.artikel_id &&
              b.lagerort_id === daten.quelle_id &&
              b.menge > 0
          )
          .sort((a, b) => b.menge - a.menge); // beste Box zuerst

        return (
          <Box
            key={pos.id}
            sx={{
              mt: 2,
              mb: 3,
              p: 1.5,
              border: '1px solid #999',
              borderRadius: 1,
              '@media print': {
                borderColor: '#000',
                pageBreakInside: 'avoid',
              },
            }}
          >
            {/* EINZEILIGE Kopfzeile: Bild + SKU + Titel + Menge */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              sx={{ mb: 1 }}
            >
              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                <ArtikelThumbnailAny
                  src={pos.bild_url || null}
                  url={pos.bild_url || null}
                  alt={pos.sku}
                  size={50}
                />

                <Typography variant="subtitle1" sx={{ lineHeight: 1.2, minWidth: 0 }}>
                  <strong>{pos.sku}</strong>
                  <span> – </span>
                  <span
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'bottom',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={pos.bezeichnung}
                  >
                    {pos.bezeichnung}
                  </span>
                </Typography>
              </Box>

              <Typography variant="subtitle1" sx={{ whiteSpace: 'nowrap' }}>
                Menge: <strong>{pos.menge}</strong>
              </Typography>
            </Box>

            {boxenFuerArtikel.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'red' }}>
                Keine Bestände für diesen Artikel im gewählten Sender-Lagerort gefunden.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Regal</TableCell>
                    <TableCell>Fach</TableCell>
                    <TableCell>Box</TableCell>
                    <TableCell align="right">Verfügbar</TableCell>
                    <TableCell align="right">Entnahme</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boxenFuerArtikel.map((b) => (
                    <TableRow key={`${b.lagerort_id}-${b.boxnummer}`}>
                      <TableCell>{b.regalnummer ?? '-'}</TableCell>
                      <TableCell>{b.fachnummer ?? '-'}</TableCell>
                      <TableCell>{b.boxnummer}</TableCell>
                      <TableCell align="right">{b.menge}</TableCell>
                      <TableCell align="right">__________</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        );
      })}
    </Container>
  );
}
